import { apiClient } from './api';
import { BaseService } from './BaseService';
import {
  DeliveryChallan,
  DeliveryChallanCreate,
  DeliveryChallanUpdate,
  DeliveryChallanSearchFilters,
  DeliveryChallanListResponse,
  DeliveryChallanStatusUpdate,
  DeliveryChallanFromSalesOrderRequest,
  DeliveryChallanCreationResponse
} from '../types/deliveryChallan';

class DeliveryChallanService extends BaseService<DeliveryChallan, DeliveryChallanCreate, DeliveryChallanUpdate, DeliveryChallanSearchFilters> {
  protected baseUrl = '/admin/delivery-challans';

  async createDeliveryChallan(data: DeliveryChallanCreate): Promise<DeliveryChallan> {
    return this.create(data);
  }

  async createFromSalesOrder(data: DeliveryChallanFromSalesOrderRequest): Promise<DeliveryChallanCreationResponse> {
    const response = await apiClient.post(`${this.baseUrl}/convert-from-sales-order`, data);
    return response.data;
  }

  async getDeliveryChallans(filters: DeliveryChallanSearchFilters & { page?: number; per_page?: number }): Promise<DeliveryChallanListResponse> {
    const result = await this.getList(filters);
    return {
      delivery_challans: result.items,
      total: result.total,
      page: result.page,
      per_page: result.per_page,
      total_pages: result.total_pages
    };
  }

  async getDeliveryChallan(id: string): Promise<DeliveryChallan> {
    return this.getById(id);
  }

  async getDeliveryChallanByNumber(challanNumber: string): Promise<DeliveryChallan> {
    const response = await apiClient.get(`${this.baseUrl}/number/${challanNumber}`);
    return response.data;
  }

  async updateDeliveryChallan(id: string, data: DeliveryChallanUpdate): Promise<DeliveryChallan> {
    return this.update(id, data);
  }

  async deleteDeliveryChallan(id: string): Promise<void> {
    return this.delete(id);
  }

  async updateStatus(id: string, data: DeliveryChallanStatusUpdate): Promise<DeliveryChallan> {
    const response = await apiClient.patch(`${this.baseUrl}/${id}/status`, data);
    return response.data;
  }

  async markAsDispatched(id: string, dispatchDate?: string): Promise<DeliveryChallan> {
    const response = await apiClient.post(`${this.baseUrl}/${id}/dispatch`, {
      dispatch_date: dispatchDate || new Date().toISOString().split('T')[0]
    });
    return response.data;
  }

  async markAsDelivered(id: string): Promise<DeliveryChallan> {
    const response = await apiClient.post(`${this.baseUrl}/${id}/deliver`);
    return response.data;
  }

  async downloadPDF(id: string): Promise<Blob> {
    const response = await apiClient.get(`${this.baseUrl}/${id}/pdf`, {
      responseType: 'blob'
    });
    return response.data;
  }

  async previewPDF(id: string): Promise<string> {
    const blob = await this.downloadPDF(id);
    return URL.createObjectURL(blob);
  }

  // Inherited from BaseService - no need to override
}

export const deliveryChallanService = new DeliveryChallanService();